'use client';

import React, { useCallback, useEffect, useMemo } from 'react';

import type { ChargebeeEvent, EventApiFilters } from '@/types/events';
import { getTimeRangeFrom } from '@/constant/eventTimeRanges';
import type { TimeRangeValue } from '@/constant/eventTimeRanges';
import { useFetchUser } from '@/auth/hooks';
import { useExplorer } from '@/hooks/useExplorer';
import { explorerAnalytics } from '@/lib/analytics';
import { useAutoForward } from '@/hooks/events/useAutoForward';
import { useEventPolling } from '@/hooks/events/useEventPolling';
import { listEvents } from '@/services/events/EventsService';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@components/components/ui/resizable';
import { AuthBlurGate } from '@components/ui/Auth/AuthBlurGate';
import { EventDetailPanel } from './EventDetailPanel';
import { EventEmptyState } from './EventEmptyState';
import { EventListPanel } from './EventListPanel';

// Mock events shown behind the blur gate when logged out
const MOCK_EVENTS: ChargebeeEvent[] = [
  {
    id: 'ev_Hxrl3mKSOHNE1mBj0',
    event_type: 'subscription_created',
    occurred_at: Math.floor(Date.now() / 1000) - 120,
    source: 'api',
    api_version: 'v2',
    user: 'admin@example.com',
    content: {},
  },
  {
    id: 'ev_Jz4wTmNP2vQR8kAc1',
    event_type: 'payment_succeeded',
    occurred_at: Math.floor(Date.now() / 1000) - 300,
    source: 'system',
    api_version: 'v2',
    user: null,
    content: {},
  },
  {
    id: 'ev_Kq9xVnOP3wRS9lBd2',
    event_type: 'invoice_generated',
    occurred_at: Math.floor(Date.now() / 1000) - 540,
    source: 'scheduled_job',
    api_version: 'v2',
    user: null,
    content: {},
  },
  {
    id: 'ev_Lr0yWoSQ4xST0mCe3',
    event_type: 'customer_changed',
    occurred_at: Math.floor(Date.now() / 1000) - 900,
    source: 'admin_console',
    api_version: 'v2',
    user: 'support@example.com',
    content: {},
  },
  {
    id: 'ev_Ms1zXpTR5ySU1nDf4',
    event_type: 'subscription_renewed',
    occurred_at: Math.floor(Date.now() / 1000) - 1800,
    source: 'system',
    api_version: 'v2',
    user: null,
    content: {},
  },
  {
    id: 'ev_Nt2AYqUS6zTV2oEg5',
    event_type: 'payment_refunded',
    occurred_at: Math.floor(Date.now() / 1000) - 3600,
    source: 'api',
    api_version: 'v2',
    user: 'billing@example.com',
    content: {},
  },
  {
    id: 'ev_Ou3BZrVT7AWU3pFh6',
    event_type: 'card_expired',
    occurred_at: Math.floor(Date.now() / 1000) - 5400,
    source: 'system',
    api_version: 'v2',
    user: null,
    content: {},
  },
  {
    id: 'ev_Pv4CAsWU8BXV4qGi7',
    event_type: 'subscription_cancelled',
    occurred_at: Math.floor(Date.now() / 1000) - 7200,
    source: 'portal',
    api_version: 'v2',
    user: 'customer@example.com',
    content: {},
  },
];

/**
 * Top-level Event Explorer page component.
 * Manages initial data fetching and orchestrates the split-pane layout.
 */
export const EventExplorerPage: React.FC = () => {
  const { isLoggedIn } = useFetchUser();
  const { siteId, siteName, apiKey } = useExplorer();
  const {
    events,
    isLoading,
    error,
    filters,
    sortDirection,
    nextOffset,
    hasMore,
    isLoadingMore,
    selectedEventId,
    setEvents,
    appendEvents,
    setIsLoading,
    setIsLoadingMore,
    setError,
    setNextOffset,
    setHasMore,
    setSelectedEventId,
  } = useEventExplorerStore();

  // Build API filters from UI filter state
  const buildApiFilters = useCallback((): EventApiFilters | undefined => {
    const apiFilters: EventApiFilters = {};
    let hasFilters = false;

    if (filters.source.length > 0) {
      apiFilters.source = {
        operator: filters.source.length === 1 ? 'is' : 'in',
        value: filters.source.length === 1 ? filters.source[0] : filters.source,
      };
      hasFilters = true;
    }

    if (filters.eventType.length > 0) {
      apiFilters.eventType = {
        operator: filters.eventType.length === 1 ? 'is' : 'in',
        value:
          filters.eventType.length === 1
            ? filters.eventType[0]
            : filters.eventType,
      };
      hasFilters = true;
    }

    if (filters.dateFrom != null && filters.dateTo != null) {
      apiFilters.occurredAt = {
        operator: 'between',
        value: [filters.dateFrom, filters.dateTo],
      };
      hasFilters = true;
    } else if (filters.dateFrom != null) {
      apiFilters.occurredAt = { operator: 'after', value: filters.dateFrom };
      hasFilters = true;
    } else if (filters.dateTo != null) {
      apiFilters.occurredAt = { operator: 'before', value: filters.dateTo };
      hasFilters = true;
    } else if (filters.timeRangePreset) {
      apiFilters.occurredAt = {
        operator: 'after',
        value: getTimeRangeFrom(filters.timeRangePreset as TimeRangeValue),
      };
      hasFilters = true;
    }

    if (filters.webhookStatus) {
      apiFilters.webhookStatus = {
        operator: 'is',
        value: filters.webhookStatus,
      };
      hasFilters = true;
    }

    if (filters.searchQuery) {
      apiFilters.id = {
        operator: 'starts_with',
        value: filters.searchQuery,
      };
      hasFilters = true;
    }

    return hasFilters ? apiFilters : undefined;
  }, [filters]);

  // Fetch initial page of events
  const fetchEvents = useCallback(async () => {
    if (!siteId || !siteName || !apiKey) return;

    // If events are already loaded (e.g. returning to this tab), do a
    // background refresh without showing a loading spinner or clearing
    // the current selection.
    const store = useEventExplorerStore.getState();
    const isInitialLoad = store.events.length === 0;

    if (isInitialLoad) {
      setSelectedEventId(null);
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await listEvents({
        siteId,
        siteName,
        apiKey,
        limit: 50,
        sortBy: sortDirection,
        filters: buildApiFilters(),
      });

      const eventList = response.list.map((item) => item.event);
      setEvents(eventList);
      setNextOffset(response.next_offset ?? null);
      setHasMore(!!response.next_offset);

      // Auto-select first event if nothing is currently selected
      const currentSelection = useEventExplorerStore.getState().selectedEventId;
      if (!currentSelection && eventList.length > 0) {
        setSelectedEventId(eventList[0].id);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load events';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [
    siteId,
    siteName,
    apiKey,
    sortDirection,
    buildApiFilters,
    setEvents,
    setSelectedEventId,
    setNextOffset,
    setHasMore,
    setIsLoading,
    setError,
  ]);

  // Load more events (infinite scroll)
  const loadMore = useCallback(async () => {
    if (!siteId || !siteName || !apiKey || !nextOffset || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const response = await listEvents({
        siteId,
        siteName,
        apiKey,
        limit: 50,
        offset: nextOffset,
        sortBy: sortDirection,
        filters: buildApiFilters(),
      });

      const eventList = response.list.map((item) => item.event);
      appendEvents(eventList, response.next_offset ?? null);
    } catch {
      // Don't set global error — it would replace the already-loaded event list
      // with an error screen. The "Load more" button will reappear for retry.
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    siteId,
    siteName,
    apiKey,
    nextOffset,
    isLoadingMore,
    sortDirection,
    buildApiFilters,
    appendEvents,
    setIsLoadingMore,
  ]);

  // Clear selection when the site changes so stale event IDs don't persist
  const prevSiteIdRef = React.useRef(siteId);
  useEffect(() => {
    if (prevSiteIdRef.current !== siteId) {
      prevSiteIdRef.current = siteId;
      setSelectedEventId(null);
    }
  }, [siteId, setSelectedEventId]);

  // Track page view and fetch events on mount
  useEffect(() => {
    explorerAnalytics.eventPageViewed();
  }, []);

  // Fetch events on mount and when filters/sort change (skip when logged out)
  useEffect(() => {
    if (isLoggedIn) {
      fetchEvents();
    }
  }, [fetchEvents, isLoggedIn]);

  // Treat missing credentials as "still loading" — the beehive /dynamic-data
  // call hasn't resolved yet, so fetchEvents() bails out silently.
  const credentialsReady = !!(siteId && siteName && apiKey);

  // Live polling for new events — memoize to avoid new object reference every render
  // (getTimeRangeFrom uses Date.now(), so calling buildApiFilters() directly would
  // create a new object each render, causing an infinite re-render loop)
  // Disable polling entirely when logged out.
  const apiFilters = useMemo(() => buildApiFilters(), [buildApiFilters]);
  const { forceRefresh } = useEventPolling({
    enabled: credentialsReady && !!isLoggedIn,
    filters: apiFilters,
  });
  useAutoForward();

  return (
    <AuthBlurGate
      heading="Explore Chargebee webhook events"
      description="Login to inspect event payloads, track webhook delivery, and forward events to your server."
    >
      <div className="h-[calc(100vh-50px)] bg-fixed bg-gradient-to-b from-[rgba(255,255,255,0.5)] to-[rgba(255,255,255,0)] via-[hsl(185,52%,90%)]">
        <div className="h-[calc(100vh-50px)] bg-fixed bg-gradient-to-l from-[rgba(255,255,255,0.5)] to-[rgba(255,255,255,0)] via-[hsl(185,60%,95%)]">
          <ResizablePanelGroup
            direction="horizontal"
            className="h-[calc(100vh-50px)] w-full"
          >
            <ResizablePanel
              defaultSize={45}
              minSize={30}
              className="flex flex-col"
            >
              <EventListPanel
                events={isLoggedIn ? events : MOCK_EVENTS}
                isLoading={isLoggedIn ? isLoading || !credentialsReady : false}
                isLoadingMore={isLoadingMore}
                error={error}
                hasMore={isLoggedIn ? hasMore : false}
                selectedEventId={
                  isLoggedIn ? selectedEventId : MOCK_EVENTS[0].id
                }
                onSelectEvent={setSelectedEventId}
                onLoadMore={loadMore}
                onRefresh={fetchEvents}
                onPollRefresh={forceRefresh}
              />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={55} minSize={35} className="bg-white">
              {!isLoggedIn ? (
                <EventEmptyState
                  type="no-selection"
                  message="Select an event to view its payload and webhook delivery details."
                />
              ) : selectedEventId ? (
                <EventDetailPanel />
              ) : (
                <EventEmptyState
                  type="no-selection"
                  message="Send Chargebee event payloads to a local or remote server directly from your browser to test webhook integrations. Events are routed through a lightweight proxy to bypass CORS restrictions."
                />
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>
    </AuthBlurGate>
  );
};
